import { useEffect, useRef, useState, useCallback } from "react";

/**
 * Tracks user inactivity by listening to DOM events on window.
 *
 * Listens to: mousemove, keydown, click, scroll, touchstart.
 * After `idleMs` of NO events, `isIdle` flips to true.
 * Any event resets the timer.
 *
 * Returns:
 *   isIdle         — boolean, true when the user has been inactive for > idleMs
 *   resetIdleTimer — function to manually reset the timer
 */
export const useIdleTimer = ({ idleMs, events }) => {
  const [isIdle, setIsIdle] = useState(false);
  const timerRef = useRef(null);
  const isIdleRef = useRef(false);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const resetIdleTimer = useCallback(() => {
    clearTimer();
    if (isIdleRef.current) {
      isIdleRef.current = false;
      setIsIdle(false);
    }
    timerRef.current = setTimeout(() => {
      isIdleRef.current = true;
      setIsIdle(true);
    }, idleMs);
  }, [clearTimer, idleMs]);

  // Single effect: register listeners and clean up. The setTimeout-based
  // arming lives in resetIdleTimer and is fired lazily on the first user
  // event (or by callers of resetIdleTimer). The setState inside
  // resetIdleTimer happens either inside an event handler (external DOM
  // trigger) or inside the setTimeout callback (delayed) — both are
  // outside React's render cycle, so they don't trigger cascading renders.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const handler = () => {
      resetIdleTimer();
    };

    events.forEach((evt) => {
      window.addEventListener(evt, handler, { passive: true });
    });

    // Arm the timer on mount via setTimeout(0) so the setState inside
    // resetIdleTimer fires outside this effect's synchronous body.
    const armTimer = setTimeout(() => {
      resetIdleTimer();
    }, 0);

    return () => {
      clearTimeout(armTimer);
      events.forEach((evt) => {
        window.removeEventListener(evt, handler);
      });
      clearTimer();
    };
  }, [events, resetIdleTimer, clearTimer]);

  return { isIdle, resetIdleTimer };
};
